/**
 * StandingWaveSkillEffect — Earth tree wall fork 2 active (standing_wave).
 *
 * While stationary (≥1000ms), the player emits a radial shockwave —
 * 14 damage and 2-tile knockback to all enemies within 4 tiles.
 * The cooldown starts only on a successful activation; a stationary-gate
 * failure leaves the cooldown untouched.
 *
 * Upgrade interactions resolved at activation time:
 * - Undertow (standing_wave_upgrade_1): +1 knockback tile (total: 3 tiles);
 *   creates a rough terrain zone (60% speed, roughTerrainDurationMs) at
 *   each enemy's pre-knockback world position.
 * - Resonant (standing_wave_upgrade_2): +8 damage, +1 radius tile.
 *   Debuffed enemies (any slow/stagger/debuff status) take 25% bonus damage.
 * - Held Ground (standing_wave_passive): +20% damage while Fortify's shield
 *   is active. Applied by the game layer via UpgradeEffectResolver — this
 *   class queries the bonus through resolveDamage() when the key is present.
 *
 * Cooldown tier: long (2000ms).
 * Base class: InstantSkillEffect (with cooldown management override).
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Wall Fork 2 — Standing Wave Sub-Path
 */

import { InstantSkillEffect } from '../../InstantSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface StandingWaveDeps {
  readonly stationary: {
    /**
     * Returns true when the player has been continuously stationary for at
     * least `thresholdMs` ms. The same StationaryTracker instance is shared
     * with Bedrock, TremorSense, IronSeam, and DeepRoot.
     */
    isStationary(thresholdMs: number): boolean;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    /** Aggregate a key from all unlocked passives within the named tree. */
    getTreePassiveEffect(treeName: string, key: string, aggregation: 'sum' | 'max'): number;
  };
  /**
   * Exposes live Fortify shield state.
   * Held Ground (standing_wave_passive) queries fortifyShieldHp > 0 to determine
   * whether the 20% damage bonus is active. Set by FortifySkillEffect on activation;
   * zeroed on shield break or movement-linger expiry.
   */
  readonly playerState: {
    readonly fortifyShieldHp: number;
  };
  readonly targeting: {
    /** Returns enemy IDs within the given pixel radius of the player. */
    getEnemiesInRadius(radiusPx: number): string[];
    /** Returns the world position of the named enemy. */
    getEnemyPosition(enemyId: string): { x: number; y: number };
    /** Returns the player's current world position. */
    getPlayerPosition(): { x: number; y: number };
  };
  readonly combat: {
    applyDamage(
      targetId: string,
      damage: number,
      opts: { sourceSkillId: string },
    ): void;
    /**
     * Pushes the target in normalizedDir by the given number of tiles.
     * normalizedDir is a unit vector pointing from the player to the enemy.
     */
    applyKnockback(
      targetId: string,
      normalizedDir: { x: number; y: number },
      tiles: number,
    ): void;
  };
  readonly statusEffects: {
    /**
     * Returns true if the target has any active slow (speedMultiplier < 1.0),
     * stagger, or debuff-category status. Used by Resonant's per-target bonus.
     *
     * Implementation: has('stagger') || has('slow')
     *   || getAll().some(e => e.def.category === 'debuff')
     */
    isDebuffed(targetId: string): boolean;
  };
  readonly zones: {
    create(config: {
      type: string;
      position: { x: number; y: number };
      radiusTiles: number;
      durationMs: number;
      speedMultiplier: number;
      sourceSkillId: string;
    }): string;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface StandingWaveConfig {
  /** Long cooldown tier. */
  readonly cooldownMs: number;
  /** Base AoE damage before Resonant's +8 delta. */
  readonly baseDamage: number;
  /** Base AoE radius in tiles before Resonant's +1 delta. */
  readonly baseRadiusTiles: number;
  /** Base knockback distance in tiles before Undertow's +1 delta. */
  readonly baseKnockbackTiles: number;
  /** Stationary duration required before activation is allowed. */
  readonly stationaryRequirementMs: number;
  /** World units per tile — converts tile distances to pixels. */
  readonly tileSizePx: number;
  /** Radius of each Undertow rough-terrain zone in tiles. */
  readonly roughTerrainZoneRadiusTiles: number;
  /** Speed multiplier applied inside Undertow rough-terrain zones (0.6 = 60%). */
  readonly roughTerrainSpeedMultiplier: number;
}

const DEFAULT_CONFIG: StandingWaveConfig = {
  cooldownMs: 2000,                   // long cooldown tier
  baseDamage: 14,                     // base AoE damage
  baseRadiusTiles: 4,                 // 4-tile base radius
  baseKnockbackTiles: 2,              // 2-tile base knockback
  stationaryRequirementMs: 1000,      // 1000ms stationary threshold
  tileSizePx: 32,                     // TILE_SIZE constant
  roughTerrainZoneRadiusTiles: 1.0,   // zone radius per Undertow placement
  roughTerrainSpeedMultiplier: 0.6,   // 60% move speed in Undertow zone
};

// ---------------------------------------------------------------------------
// Knockback record (per hit, per activation)
// ---------------------------------------------------------------------------

export interface KnockbackRecord {
  readonly targetId: string;
  /** Normalized direction unit vector from player to enemy. */
  readonly normalizedDir: { x: number; y: number };
  /** Knockback distance in tiles. */
  readonly tiles: number;
  /** Enemy's world position at the moment the knockback fires. */
  readonly preKnockbackPos: { x: number; y: number };
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class StandingWaveSkillEffect extends InstantSkillEffect {
  readonly skillId = 'standing_wave';

  private readonly deps: StandingWaveDeps;
  private readonly config: StandingWaveConfig;

  // Cooldown state
  private _cooldownRemaining = 0;

  // Per-activation telemetry (readable by simulation scenarios)
  private _lastHitCount = 0;
  private _lastDamageDealt = 0;
  private _lastKnockbacks: KnockbackRecord[] = [];
  private _lastZoneIds: string[] = [];
  private _lastZonePositions: Array<{ x: number; y: number }> = [];
  private _activationCount = 0;
  /** True when Held Ground's 20% bonus was applied on the last activation. */
  private _heldGroundActive = false;

  constructor(deps: StandingWaveDeps, config: Partial<StandingWaveConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  override get cooldown(): number { return this._cooldownRemaining; }
  get lastHitCount(): number { return this._lastHitCount; }
  get lastDamageDealt(): number { return this._lastDamageDealt; }
  get lastKnockbacks(): readonly KnockbackRecord[] { return this._lastKnockbacks; }
  get lastZoneIds(): readonly string[] { return this._lastZoneIds; }
  get lastZonePositions(): ReadonlyArray<{ x: number; y: number }> { return this._lastZonePositions; }
  get activationCount(): number { return this._activationCount; }
  get stationaryRequirementMs(): number { return this.config.stationaryRequirementMs; }
  /** Whether Held Ground's 20% bonus was applied on the most recent activation. */
  get heldGroundActive(): boolean { return this._heldGroundActive; }

  // --- InstantSkillEffect interface ---

  /**
   * Returns true only when: (a) cooldown has fully elapsed, and (b) the
   * player has been stationary for at least stationaryRequirementMs.
   *
   * A stationary-gate failure never starts the cooldown — the skill simply
   * does nothing and the next call can try again immediately.
   */
  canActivate(): boolean {
    if (this._cooldownRemaining > 0) return false;
    return this.deps.stationary.isStationary(this.config.stationaryRequirementMs);
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve upgrade deltas from UpgradeEffectResolver
    const radiusDelta = this.deps.resolver.getAdditiveEffect('standing_wave', 'radiusTiles');
    const damageDelta = this.deps.resolver.getAdditiveEffect('standing_wave', 'damage');
    const knockbackDelta = this.deps.resolver.getAdditiveEffect('standing_wave', 'knockbackTiles');
    const debuffedBonus = this.deps.resolver.getAdditiveEffect('standing_wave', 'debuffedBonusDamage');
    const roughTerrainDurationMs = this.deps.resolver.getAdditiveEffect(
      'standing_wave',
      'roughTerrainDurationMs',
    );

    // Held Ground (standing_wave_passive): +20% damage while Fortify's shield is active.
    // Applied once per activation before the target loop (per-cast, not per-target).
    // Multiplication order: base damage → Held Ground → Resonant debuff multiplier.
    const fortifyBonus = this.deps.resolver.getTreePassiveEffect(
      'earth',
      'fortifyActiveBonusDamage',
      'sum',
    );
    this._heldGroundActive = fortifyBonus > 0 && this.deps.playerState.fortifyShieldHp > 0;

    const effectiveRadiusTiles = this.config.baseRadiusTiles + radiusDelta;
    const baseEffectiveDamage = this.config.baseDamage + damageDelta;
    // Apply Held Ground multiplier (1.0 when inactive — no numeric change for existing paths)
    const effectiveDamage = this._heldGroundActive
      ? baseEffectiveDamage * (1 + fortifyBonus)
      : baseEffectiveDamage;
    const effectiveKnockbackTiles = this.config.baseKnockbackTiles + knockbackDelta;
    const effectiveRadiusPx = effectiveRadiusTiles * this.config.tileSizePx;

    const playerPos = this.deps.targeting.getPlayerPosition();
    const targets = this.deps.targeting.getEnemiesInRadius(effectiveRadiusPx);

    this._lastKnockbacks = [];
    this._lastZoneIds = [];
    this._lastZonePositions = [];
    let totalDamage = 0;

    for (const targetId of targets) {
      const enemyPos = this.deps.targeting.getEnemyPosition(targetId);
      const preKnockbackPos = { x: enemyPos.x, y: enemyPos.y };

      // Normalized direction from player to enemy (knockback vector)
      const dx = enemyPos.x - playerPos.x;
      const dy = enemyPos.y - playerPos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const nx = dist > 0 ? dx / dist : 0;
      const ny = dist > 0 ? dy / dist : 1;
      const normalizedDir = { x: nx, y: ny };

      // Damage: Resonant debuff bonus is per-target (applied after Held Ground per-activation base)
      let damage = effectiveDamage;
      if (debuffedBonus > 0 && this.deps.statusEffects.isDebuffed(targetId)) {
        damage = damage * (1 + debuffedBonus);
      }
      const finalDamage = Math.round(damage);

      this.deps.combat.applyDamage(targetId, finalDamage, { sourceSkillId: 'standing_wave' });
      totalDamage += finalDamage;

      // Knockback — radial push away from player
      this.deps.combat.applyKnockback(targetId, normalizedDir, effectiveKnockbackTiles);
      this._lastKnockbacks.push({
        targetId,
        normalizedDir,
        tiles: effectiveKnockbackTiles,
        preKnockbackPos,
      });

      // Undertow: rough terrain zone at each enemy's pre-knockback position
      if (roughTerrainDurationMs > 0) {
        const zoneId = this.deps.zones.create({
          type: 'rough_terrain',
          position: preKnockbackPos,
          radiusTiles: this.config.roughTerrainZoneRadiusTiles,
          durationMs: roughTerrainDurationMs,
          speedMultiplier: this.config.roughTerrainSpeedMultiplier,
          sourceSkillId: 'standing_wave',
        });
        this._lastZoneIds.push(zoneId);
        this._lastZonePositions.push({ x: preKnockbackPos.x, y: preKnockbackPos.y });
      }
    }

    this._lastHitCount = targets.length;
    this._lastDamageDealt = totalDamage;
    this._activationCount++;
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  override update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  override destroy(): void {
    this._cooldownRemaining = 0;
  }
}
